import { captureException } from '@sentry/nextjs'
import { getS3Client } from '../s3-client'
import { paginateListObjectsV2 } from '@aws-sdk/client-s3'
import dayjs from 'dayjs'
import type { S3ClientCredentials } from '../s3-client'

export const getFigures = async (
  documents: Array<{
    jurisdiction: string
    publicationDate: string
    kind: string
    documentNumber: string
  }>,
  credentials: S3ClientCredentials
): Promise<Record<string, string[]>> => {
  const s3 = getS3Client({
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey,
  })

  const bucketName = 'ktmine'
  const keyPrefix = 'patent-images'

  const result: Record<string, { imagePaths: string[]; error?: Error }> = {}

  await Promise.allSettled(
    documents.map(
      async ({ jurisdiction, publicationDate, kind, documentNumber }) => {
        const publicationYear = dayjs(publicationDate).get('year')
        const prefix = `${keyPrefix}/${jurisdiction}/${publicationYear}/${kind}/${documentNumber}/`

        try {
          const imagePaths: string[] = []
          const pages = paginateListObjectsV2(
            { client: s3 },
            { Bucket: bucketName, Prefix: prefix }
          )

          for await (const page of pages) {
            if (page.Contents) {
              const mappedPage = page.Contents.map((object) => object.Key!)
                .filter(Boolean)
                .filter((key) =>
                  /\.(jpe?g|png|gif|bmp|webp|svg|tiff?|jfif|jpe|jif|exif|ppm|pgm|pbm|pnm|apng|cur)$/i.test(
                    key
                  )
                )
              imagePaths.push(...mappedPage)
            }
          }

          result[documentNumber] = { imagePaths }
        } catch (error) {
          result[documentNumber] = {
            imagePaths: [],
            error:
              error instanceof Error
                ? error
                : new Error(
                    `Could not fetch images from ktmine S3 for patent ${documentNumber}`
                  ),
          }
        }
      }
    )
  )
  for (const { error } of Object.values(result)) {
    if (error) {
      captureException(error)
    }
  }

  const parsedResult: Record<string, string[]> = Object.fromEntries(
    Object.entries(result).map(([key, value]) => [key, value.imagePaths])
  )

  return parsedResult
}
